'use strict';

ngtk.rpcClient = {};

ngtk.rpcClient.Request = Class({

    /**
     * @type {angular.$q}
     */
    _q: undefined,

    _errorHandler: undefined,
    _action: undefined,
    _args: undefined,

    /**
     * @param {ngtk.rpcClient.Client} client
     * @param {string} action
     * @param {*} args
     * @constructor
     */
    construct: function(client, action, args) {
        this._client = client;
        this._q = client._q;
        this.setErrorHandler(client.getErrorHandler());
        this._action = action.replace(/^\/+/, '');
        this._args = args || {};
    },

    /**
     * @param {Function=} handler
     * @returns {ngtk.rpcClient.Request}
     */
    setErrorHandler: function(handler) {
        if (handler) {
            if (!angular.isFunction(handler)) {
                throw new Error("handler must be a function");
            }
            this._errorHandler = handler;
        } else {
            this._errorHandler = undefined;
        }
        return this;
    },

    /**
     * @returns {angular.promise}
     */
    execute: function() {
        return this._client.executeRequest(this).then(
            function(response) {
                if (!response.data) {
                    return this.reject();
                }
                var result = this.parseResponse(response.data);
                var rejection = this.checkForErrors(result);
                if (angular.isDefined(rejection)) {
                    return rejection;
                }
                return result;
            }.bind(this),
            function(error) {
                if (error.status == 0 && error.data === null) {
                    return;
                }
                var errors = {
                    '404': 'not_found',
                    '403': 'access_denied',
                    '400': 'internal_error',
                    '405': 'internal_error'
                };
                return this.reject({
                    error: errors[error.status] || 'network_error',
                    error_details: error.status
                });
            }.bind(this)
        );
    },

    checkForErrors: function(result) {
        if (result.$page && result.uri) {
            var error = undefined;
            switch (result.uri) {
                case '/locked':
                    error = 'current_user_is_locked';
                    break;
            }
            return this._q.reject(error);
        }

        if (this._errorHandler) {
            var rejection = this._errorHandler(result);
            if (rejection) {
                return this._q.reject(rejection);
            } else {
                return undefined;
            }
        }

        if (result.$$failure) {
            return this.reject(result);
        }
        return undefined;
    },

    reject: function(error) {
        return this._q.reject(error);
    },

    /**
     * @param {*} data
     * @returns {*}
     */
    parseResponse: function(data) {
        if (!angular.isObject(data)) {
            return {
                $$failure: true,
                error: 'protocol_error',
                error_details: 'empty_response'
            };
        }
        if (data.error || data.errors) {
            data.$$failure = true;
        }
        if (data.$redirect && data.uri) {
            window.location = data.uri;
            return {
                error: 'session_expired'
            };
        }
        return data;
    },

    /**
     * @abstract
     * @return {*}
     */
    buildRequest: function(baseUrl) {
        throw new Error("Abstract method not implemented");
    },

    _filterArgs: function(args) {
        var result = {};
        angular.forEach(args, function(value, key) {
            if (key !== '$model') {
                result[key] = value;
            }
        });
        return result;
    }

});

ngtk.rpcClient.GetRequest = ngtk.rpcClient.Request.extend({

    buildRequest: function(baseUrl) {
        var qsArgs = {};
        var url = new Uri(ngtk.tools.string.parseTemplate(baseUrl + this._action, this._args, qsArgs));
        angular.forEach(qsArgs, function(value, key) {
            if (angular.isDefined(value)) {
                url.addQueryParam(key, value);
            }
        });
        return {
            method: 'GET',
            url: '' + url
        };
    }

});

ngtk.rpcClient.PostRequest = ngtk.rpcClient.Request.extend({

    buildRequest: function(baseUrl) {
        return {
            method: 'POST',
            url: baseUrl + this._action,
            data: this._filterArgs(this._args)
        };
    }

});

ngtk.rpcClient.Client = Class({

    $inject: ['$http', '$q', 'rpc_url'],

    cors_proxy_url: undefined,

    /**
     * @param {angular.$http}
     */
    _http: undefined,
    _q: undefined,
    _rpc_url: null,
    _errorHandlerFn: null,

    _cancelPromises: null,

    /**
     * @param {angular.$http} $http
     * @param {angular.$q} $q
     * @param {string=} rpc_url
     */
    construct: function($http, $q, rpc_url) {
        this._http = $http;
        this._q = $q;
        rpc_url && this.setRpcUrl(rpc_url);
        this._cancelPromises = [];
    },

    setCorsProxyUrl: function(cors_proxy_url) {
        this.cors_proxy_url = cors_proxy_url;
        return this;
    },

    /**
     * @param {string} rpc_url
     */
    setRpcUrl: function(rpc_url) {
        var url = new Uri(rpc_url);
        if (this._isCorsProxyRequired() && this.cors_proxy_url) {
            this._rpc_url = new Uri(this.cors_proxy_url + url.host() + url.path());
        } else {
            this._rpc_url = url;
        }
    },

    _isCorsProxyRequired: function() {
        return (typeof window.XMLHttpRequest === 'undefined' || !("withCredentials" in new window.XMLHttpRequest));
    },

    /**
     * @param {string} method
     * @param {string} action
     * @param {*} [args]
     * @returns {ngtk.rpcClient.Request}
     */
    request: function(method, action, args) {
        switch (method.toLowerCase()) {
            case 'get':
                return this.get(action, args);
            case 'post':
                return this.post(action, args);
        }
        throw new Error("Invalid method argument: " + method);
    },

    /**
     * @param {string} action
     * @param {*} [args]
     * @returns {ngtk.rpcClient.GetRequest}
     */
    get: function(action, args) {
        return new ngtk.rpcClient.GetRequest(this, action, args);
    },

    /**
     * @param {string} action
     * @param {*} [args]
     * @returns {ngtk.rpcClient.PostRequest}
     */
    post: function(action, args) {
        return new ngtk.rpcClient.PostRequest(this, action, args);
    },

    /**
     * @returns {Function}
     */
    getErrorHandler: function() {
        return this._errorHandlerFn;
    },

    cancelAll: function() {
        var cancelPromise;
        while (angular.isDefined(cancelPromise = this._cancelPromises.shift())) {
            cancelPromise.resolve();
        }
    },

    /**
     * @param {ngtk.rpcClient.Request} request
     * @returns {angular.promise}
     */
    executeRequest: function(request) {
        var cancelPromise = this._q.defer();
        this._cancelPromises.push(cancelPromise);
        return this._http(angular.extend({
            timeout: cancelPromise,
            headers: {
                'X-Rpc': 1,
                'X-Xhr-Ref': (function(uri) {
                    return uri.path() + uri.query();
                })(new Uri(window.location))
            }
        }, request.buildRequest(this._getUrl()))).finally(function() {
            var index = this._cancelPromises.indexOf(cancelPromise);
            if (index >= 0) {
                this._cancelPromises.splice(index, 1);
            }
        }.bind(this));
    },

    _getUrl: function() {
        return ('' + this._rpc_url).replace(/\/*$/, '/');
    }

});
